/**
 * RegistrationTeam.status — the ADMIN-SET status of a registration.
 *
 * This is one of TWO orthogonal axes. Don't confuse it with the other:
 *
 *   status (HERE, stored)      what the admin has decided about this team
 *   CapacityState (derived)    whether the team holds a seat, from payment + FIFO
 *                              — see ./waitlist.ts
 *
 * Both vocabularies contain the words "pending" and "waitlist", which is exactly
 * why they are kept in separate modules with separate types. A team can be
 * status `''` ("Pending" — the admin hasn't acted) while its capacity state is
 * `'secured'`, and vice versa.
 *
 * Two of the four values DO override the capacity derivation:
 *   - 'waitlisted' forces the team onto the waitlist regardless of payment/FIFO
 *   - 'withdrawn'  removes it from the capacity system entirely
 * `'accepted'` deliberately does NOT — it stays the admin lock-in it always was,
 * so an admin can't silently push a division past its capacity. See ADR 0027.
 *
 * STORAGE GOTCHA: the Mongoose model declares a bare `status: String` with no
 * default, so on most existing documents the field is ABSENT, not `''`. Only the
 * Zod schema defaults to `''`. Always read through `normalizeStatus` — never
 * compare `rt.status` directly.
 */

export type RegistrationTeamStatus = '' | 'accepted' | 'waitlisted' | 'withdrawn';

/** Ordered options — the single source of truth for the dropdown and every badge. */
export const REGISTRATION_TEAM_STATUS_OPTIONS: Array<{
  value: RegistrationTeamStatus;
  label: string;
  description: string;
}> = [
  {
    value: '',
    label: 'Pending',
    description: 'Registered; the admin has not placed the team yet.',
  },
  {
    value: 'accepted',
    label: 'Accepted',
    description: 'Officially placed in its division and shown on the public site.',
  },
  {
    value: 'waitlisted',
    label: 'Waitlist',
    description: 'Forced onto the waitlist, whatever the payment order says.',
  },
  {
    value: 'withdrawn',
    label: 'Withdrawn',
    description: 'Dropped out. Hidden everywhere public and uncounted, but kept on the registration.',
  },
];

const VALID: RegistrationTeamStatus[] = REGISTRATION_TEAM_STATUS_OPTIONS.map((o) => o.value);

/**
 * Collapses everything the field can actually hold into one of the four values.
 * Handles `undefined` (the common case — see the storage gotcha above), `''`,
 * and the legacy `'accept'` written by the retired /admin app.
 */
export function normalizeStatus(teamOrStatus: any): RegistrationTeamStatus {
  const raw =
    typeof teamOrStatus === 'string' || teamOrStatus == null
      ? teamOrStatus
      : teamOrStatus.status;
  const value = String(raw ?? '').trim().toLowerCase();
  if (value === 'accept') return 'accepted'; // legacy /admin value
  return (VALID.indexOf(value as RegistrationTeamStatus) >= 0
    ? value
    : '') as RegistrationTeamStatus;
}

/** True when the value is one this app can store (used to validate API input). */
export function isValidStatus(value: any): boolean {
  return typeof value === 'string' && VALID.indexOf(value as RegistrationTeamStatus) >= 0;
}

/** Dropped out: excluded from capacity, counts, and every public surface. */
export function isWithdrawn(teamOrStatus: any): boolean {
  return normalizeStatus(teamOrStatus) === 'withdrawn';
}

/** Admin has forced this team onto the waitlist, overriding payment + FIFO. */
export function isForcedWaitlist(teamOrStatus: any): boolean {
  return normalizeStatus(teamOrStatus) === 'waitlisted';
}

/** Admin lock-in. Says nothing about whether the team holds a seat. */
export function isAccepted(teamOrStatus: any): boolean {
  return normalizeStatus(teamOrStatus) === 'accepted';
}

/**
 * Radix's <Select.Item> THROWS on `value=""` — it reserves the empty string for
 * "clear the selection and show the placeholder". Pending is stored as `''`, so
 * dropdowns need a non-empty stand-in. Convert at the UI boundary with the two
 * helpers below and keep storing `''`; doing it here rather than inline in each
 * component means the constraint is handled once.
 */
export const PENDING_UI_VALUE = 'pending';

/** Stored status → the value a <Select> can safely use. */
export function toSelectValue(teamOrStatus: any): string {
  const status = normalizeStatus(teamOrStatus);
  return status === '' ? PENDING_UI_VALUE : status;
}

/** A <Select> value → the status to store. */
export function fromSelectValue(value: any): RegistrationTeamStatus {
  // normalizeStatus already folds the unrecognized PENDING_UI_VALUE to '', but
  // being explicit keeps the round-trip obvious to the next reader.
  return value === PENDING_UI_VALUE ? '' : normalizeStatus(value);
}

/** Human label for any stored value, including absent/legacy ones. */
export function getStatusLabel(teamOrStatus: any): string {
  const status = normalizeStatus(teamOrStatus);
  const option = REGISTRATION_TEAM_STATUS_OPTIONS.filter((o) => o.value === status)[0];
  return option ? option.label : 'Pending';
}
